import type { GraphClient } from '@atbash-labs/ontology-graph'
import type { TagMappingTypeDefinition } from './definition'
import type { EnumJson } from './enum-maps'
import {
  segmentTagName,
  type CompiledGrammar,
  type GrammarToken,
  type GrammarSlot,
  type TagSegment
} from './grammar'

import { loadTagMappingRecords } from './graph'
import {
  compileGrammarPair,
  compileTagMappingSpec,
  listGrammarPairs,
  MANUAL_GRAMMAR_ID,
  matchTagSets,
  type ParsedTag,
  type TagGrammarPair,
  type TagMappingSpec,
  type TagMatch,
  type TagRecord
} from './match'

const SAMPLE_TAG_CAP = 5
const RESULT_CAP = 20_000

export interface TagMappingIssue {
  severity: 'error' | 'warning'
  message: string
}

export interface TagMappingPreviewRow {
  aName: string
  aId?: string
  bName: string
  bId?: string
  slots: Record<string, string>
  unique: boolean
  grammarId: string
}

export interface TagMappingGrammarPreview {
  id: string
  label?: string
  rationale?: string
  aGrammar: string
  bGrammar: string
  aSlots?: GrammarSlot[]
  bSlots?: GrammarSlot[]
  enums?: EnumJson
  enumMaps?: string
  aTokens: GrammarToken[]
  bTokens: GrammarToken[]
  joinKeys: string[]
  a: { matched: number; total: number }
  b: { matched: number; total: number }
  matchCount: number
  uniqueCount: number
  matches: TagMappingPreviewRow[]
  aSamples: TagNameBreakdown[]
  bSamples: TagNameBreakdown[]
}

export interface TagNameBreakdown {
  name: string
  segments: TagSegment[]
}

export interface TagMappingPreviewResult {
  joinKeys: string[]
  grammars: TagMappingGrammarPreview[]
  a: { total: number; parsed: number; unparsed: number }
  b: { total: number; parsed: number; unparsed: number }
  matchCount: number
  uniqueCount: number
  ambiguousCount: number
  matches: TagMappingPreviewRow[]
  manualMatches: TagMappingPreviewRow[]
  unmatchedA: Array<{ name: string; id?: string }>
  unmatchedB: Array<{ name: string; id?: string }>
  unparsedA: Array<{ name: string; id?: string }>
  unparsedB: Array<{ name: string; id?: string }>
  /** Every loaded A name, including those no grammar parsed. */
  aNames: string[]
  /** Every loaded B name, including those no grammar parsed. */
  bNames: string[]
  issues: TagMappingIssue[]
}

export interface PreviewTagMappingInput extends TagMappingSpec {
  definition?: TagMappingTypeDefinition
  siteId?: string
  organizationId?: string
  graph?: GraphClient
  /** Preloaded candidates for non-graph mapping sources such as PI Web API. */
  aRecords?: readonly TagRecord[]
  bRecords?: readonly TagRecord[]
  aSamples?: readonly string[]
  bSamples?: readonly string[]
}

export async function previewTagMapping(
  input: PreviewTagMappingInput
): Promise<TagMappingPreviewResult> {
  if (listGrammarPairs(input).length > 0) compileTagMappingSpec(input)
  const aTags = uniqueRecords([...(input.aRecords ?? []), ...samplesToRecords(input.aSamples, 'a')])
  const bTags = uniqueRecords([...(input.bRecords ?? []), ...samplesToRecords(input.bSamples, 'b')])
  const issues: TagMappingIssue[] = []

  if (input.graph && input.siteId?.trim()) {
    if (!input.definition) {
      throw new Error('A tag mapping type definition is required when loading graph candidates')
    }
    const fromGraph = await loadTagMappingRecords(
      input.graph,
      input.definition,
      input.siteId.trim(),
      input.organizationId
    )
    aTags.push(...fromGraph.a)
    bTags.push(...fromGraph.b)
    if (fromGraph.a.length === 0) {
      issues.push({
        severity: 'warning',
        message: `No ${input.definition.sides.a.label} found on this site yet. Preview uses sample values until its ingest finishes.`
      })
    }
    if (fromGraph.b.length === 0) {
      issues.push({
        severity: 'warning',
        message: `No ${input.definition.sides.b.label} found on this site yet. Preview uses sample values until its ingest finishes.`
      })
    }
  }

  const matched = matchTagSets(input, aTags, bTags)
  const uniqueCount = matched.matches.filter((row) => row.unique).length
  const aTotal = matched.a.total
  const bTotal = matched.b.total
  return {
    joinKeys: matched.joinKeys,
    grammars: matched.grammars.map((slice) => {
      const compiled = compileGrammarPair(slice.pair)
      const unique = slice.matches.filter((row) => row.unique).length
      const preview: TagMappingGrammarPreview = {
        id: slice.pair.id,
        aGrammar:
          slice.pair.aGrammar ??
          slice.pair.a?.map((slot) => `{${slot.name}: ${slot.type}}`).join('') ??
          '',
        bGrammar:
          slice.pair.bGrammar ??
          slice.pair.b?.map((slot) => `{${slot.name}: ${slot.type}}`).join('') ??
          '',
        aTokens: compiled.a.tokens,
        bTokens: compiled.b.tokens,
        joinKeys: slice.joinKeys,
        a: { matched: slice.a.parsed.length, total: aTotal },
        b: { matched: slice.b.parsed.length, total: bTotal },
        matchCount: slice.matches.length,
        uniqueCount: unique,
        matches: slice.matches.slice(0, RESULT_CAP).map(toPreviewRow),
        aSamples: breakdownsOf(compiled.a, slice.a.parsed),
        bSamples: breakdownsOf(compiled.b, slice.b.parsed)
      }
      if (slice.pair.label) preview.label = slice.pair.label
      if (slice.pair.rationale) preview.rationale = slice.pair.rationale
      if (slice.pair.a) preview.aSlots = slice.pair.a
      if (slice.pair.b) preview.bSlots = slice.pair.b
      if (slice.pair.enums) preview.enums = slice.pair.enums
      if (slice.pair.enumMaps) preview.enumMaps = slice.pair.enumMaps
      return preview
    }),
    a: {
      total: aTotal,
      parsed: matched.a.parsed.length,
      unparsed: matched.a.unparsed.length
    },
    b: {
      total: bTotal,
      parsed: matched.b.parsed.length,
      unparsed: matched.b.unparsed.length
    },
    matchCount: matched.matches.length,
    uniqueCount,
    ambiguousCount: matched.matches.length - uniqueCount,
    matches: matched.matches
      .filter((row) => row.grammarId !== MANUAL_GRAMMAR_ID)
      .slice(0, RESULT_CAP)
      .map(toPreviewRow),
    manualMatches: manualPreviewRows(input.manualLinks, matched.matches),
    unmatchedA: capNames(matched.unmatchedA),
    unmatchedB: capNames(matched.unmatchedB),
    unparsedA: capNames(matched.a.unparsed),
    unparsedB: capNames(matched.b.unparsed),
    aNames: uniqueNames(aTags),
    bNames: uniqueNames(bTags),
    issues
  }
}

export function dropIdleGrammarPairsFromPreview(
  grammars: readonly TagGrammarPair[],
  preview: TagMappingPreviewResult
): TagGrammarPair[] {
  const keep = new Set(preview.grammars.filter(isLivePreviewGrammar).map((slice) => slice.id))
  return grammars.filter((pair) => keep.has(pair.id))
}

export function isLivePreviewGrammar(slice: {
  matchCount: number
  a?: { matched?: number }
  b?: { matched?: number }
}): boolean {
  return slice.matchCount > 0 && (slice.a?.matched ?? 0) > 0 && (slice.b?.matched ?? 0) > 0
}

function samplesToRecords(samples: readonly string[] | undefined, prefix: string): TagRecord[] {
  return (samples ?? [])
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name, index) => ({ id: `${prefix}-sample-${index}`, name }))
}

function uniqueRecords(records: readonly TagRecord[]): TagRecord[] {
  const seen = new Set<string>()
  const out: TagRecord[] = []
  for (const record of records) {
    const id = record.id.trim()
    const name = record.name.trim()
    if (!id || !name || seen.has(id)) continue
    seen.add(id)
    out.push({ id, name })
  }
  return out
}

function manualPreviewRows(
  stored: readonly { aName: string; bName: string }[] | undefined,
  matches: readonly TagMatch[]
): TagMappingPreviewRow[] {
  const resolved = matches
    .filter((row) => row.grammarId === MANUAL_GRAMMAR_ID)
    .slice(0, RESULT_CAP)
    .map(toPreviewRow)
  const seen = new Set(
    resolved.map((row) => `${row.aName.toUpperCase()}\0${row.bName.toUpperCase()}`)
  )
  const extras: TagMappingPreviewRow[] = []
  for (const link of stored ?? []) {
    const aName = link.aName.trim()
    const bName = link.bName.trim()
    if (!aName || !bName) continue
    const key = `${aName.toUpperCase()}\0${bName.toUpperCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    extras.push({
      aName,
      bName,
      slots: {},
      unique: true,
      grammarId: MANUAL_GRAMMAR_ID
    })
    if (resolved.length + extras.length >= RESULT_CAP) break
  }
  return [...resolved, ...extras]
}

function toPreviewRow(match: TagMatch): TagMappingPreviewRow {
  const row: TagMappingPreviewRow = {
    aName: match.a.name,
    bName: match.b.name,
    slots: match.slots,
    unique: match.unique,
    grammarId: match.grammarId
  }
  if (!match.a.id.startsWith('a-sample-')) row.aId = match.a.id
  if (!match.b.id.startsWith('b-sample-')) row.bId = match.b.id
  return row
}

function uniqueNames(tags: readonly TagRecord[]): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const tag of tags) {
    const name = tag.name.trim()
    if (!name || seen.has(name)) continue
    seen.add(name)
    names.push(name)
    if (names.length >= RESULT_CAP) break
  }
  return names
}

function capNames(
  tags: ReadonlyArray<TagRecord | ParsedTag>
): Array<{ name: string; id?: string }> {
  return tags.slice(0, RESULT_CAP).map((tag) => {
    const row: { name: string; id?: string } = { name: tag.name }
    if (!tag.id.includes('-sample-')) row.id = tag.id
    return row
  })
}

function breakdownsOf(compiled: CompiledGrammar, tags: readonly ParsedTag[]): TagNameBreakdown[] {
  const out: TagNameBreakdown[] = []
  for (const tag of tags) {
    if (out.length >= SAMPLE_TAG_CAP) break
    const segments = segmentTagName(compiled, tag.name)
    if (!segments) continue
    out.push({ name: tag.name, segments })
  }
  return out
}
